/* eslint-disable */
/**
 * Roster timeline render profiling helper (dev instrumentation).
 *
 * The controller is intentionally disabled by default. Enable it explicitly from the
 * browser console with `window.__rosterRenderPerf.enable()` before collecting a trace.
 * Instrumentation is bounded and is a no-op while disabled.
 */

const GLOBAL = (typeof window !== 'undefined') ? window : globalThis;
const GETALL = '/api/roster/assignment/get_all/';

export const MAX_EVENTS = 500;

export const perfNow = () => ((GLOBAL.performance && GLOBAL.performance.now)
  ? GLOBAL.performance.now()
  : Date.now());

function round(x) {
  return x == null ? null : Math.round(x * 100) / 100;
}

function disconnectNetworkObserver(s) {
  if (s._networkObserver) {
    try {
      s._networkObserver.disconnect();
    }
    catch (e) { /* ignore */ }
    s._networkObserver = null;
  }
}

function clearHelperMarks(s) {
  if (GLOBAL.performance && GLOBAL.performance.clearMarks) {
    (s._helperMarks || []).forEach((name) => {
      try {
        GLOBAL.performance.clearMarks(name);
      }
      catch (e) { /* ignore */ }
    });
  }
  s._helperMarks = [];
}

function clearProfilerState(s) {
  s.responseEnd = null;
  s.events = [];
  s.counts = {};
  clearHelperMarks(s);
  disconnectNetworkObserver(s);
}

function store() {
  let s = GLOBAL.__rosterRenderPerf;
  if (!s) {
    s = {
      enabled: false,
      responseEnd: null,
      events: [],
      counts: {},
      details: {
        setServerItems: false
      },
      _helperMarks: [],
      _networkObserver: null,
      enable() {
        this.enabled = true;
        initRosterNetworkObserver();
        return this;
      },
      disable() {
        this.enabled = false;
        clearProfilerState(this);
      },
      reset() {
        clearProfilerState(this);
      },
      report() {
        const re = this.responseEnd;
        const rel = t => (re != null && t != null ? round(t - re) : null);
        return {
          responseEnd_ms: re,
          counts: { ...this.counts },
          events: this.events.map(e => ({ ...e, sinceResponseEnd_ms: rel(e.t) })),
        };
      },
    };
    GLOBAL.__rosterRenderPerf = s;
  }
  return s;
}

export function enable() {
  return store().enable();
}

export function disable() {
  const s = store();
  s.disable();
  return s;
}

/** Record a named timeline event with a high-resolution timestamp. */
export function perfMark(name, extra) {
  const s = store();
  if (!s.enabled || s.events.length >= MAX_EVENTS) return;
  const t = perfNow();
  s.events.push({ name, t: round(t), ...(extra || {}) });
  try {
    if (GLOBAL.performance && GLOBAL.performance.mark) {
      const helperMark = `roster:${name}`;
      GLOBAL.performance.mark(helperMark);
      if (s._helperMarks.indexOf(helperMark) === -1) {
        s._helperMarks.push(helperMark);
      }
    }
  }
  catch (e) { /* ignore */ }
}

export const isSetServerItemsDetailEnabled = () => Boolean(
  GLOBAL.__rosterRenderPerf
  && GLOBAL.__rosterRenderPerf.enabled
  && GLOBAL.__rosterRenderPerf.details
  && GLOBAL.__rosterRenderPerf.details.setServerItems
);

export const perfEvent = (name, detail) => {
  const s = GLOBAL.__rosterRenderPerf;
  if (!s || !s.enabled) return;
  perfMark(name, { detail });
};

/** Increment a named counter and mark the ordinal occurrence. */
export function perfCount(name) {
  const s = store();
  if (!s.enabled) return 0;
  if (s.events.length >= MAX_EVENTS) return s.counts[name] || 0;
  s.counts[name] = (s.counts[name] || 0) + 1;
  perfMark(`${name}#${s.counts[name]}`);
  return s.counts[name];
}

/**
 * Install a PerformanceObserver that captures the assignment get_all
 * responseEnd (wire-level finish). Safe to call multiple times; only the
 * first observer is installed and only the first matching entry is recorded.
 */
export function initRosterNetworkObserver() {
  const s = store();
  if (!s.enabled || s._networkObserver) return;
  try {
    const po = new GLOBAL.PerformanceObserver((list) => {
      if (!s.enabled) return;
      for (const entry of list.getEntries()) {
        if (entry.name && entry.name.indexOf(GETALL) !== -1 && s.responseEnd == null) {
          s.responseEnd = round(entry.responseEnd);
          perfMark('network-responseEnd', { url: entry.name });
        }
      }
    });
    po.observe({ type: 'resource', buffered: true });
    s._networkObserver = po;
  }
  catch (e) { /* ignore */ }
}

/**
 * Schedule two nested requestAnimationFrame callbacks and mark each. Used to
 * approximate "the browser has painted the committed timeline" after the
 * loading flag flips to false.
 */
export function markNextTwoFrames(label = 'raf') {
  const s = store();
  if (!s.enabled) return;
  const raf = GLOBAL.requestAnimationFrame || (cb => setTimeout(cb, 16));
  raf(() => {
    perfMark(`${label}-frame1`);
    raf(() => {
      perfMark(`${label}-frame2-painted`);
    });
  });
}
